import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Edit,
  Trash2,
  FolderPlus,
  UserPlus,
} from "lucide-react";
import toast from "react-hot-toast";

const BASE = "https://api.livestockify.com";
const PROJECTS_URL = `${BASE}/api/urjabio/projects`;
const USERS_URL = `${BASE}/api/urjabio/users`;
const DEVICE_DATA_URL = `${BASE}/api/iot/data`;

const AdminDashboard = () => {
  const formatValue = (val) => {
    if (val === null || val === undefined || val === "-") return "-";
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return num.toFixed(3);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return null;
    const date = new Date(ts);
    return date.toLocaleString();
  };

  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [userProjectMap, setUserProjectMap] = useState({});
  const [projectDeviceData, setProjectDeviceData] = useState({});
  const [userDeviceData, setUserDeviceData] = useState({});
  const [deviceDataCache, setDeviceDataCache] = useState({});
  const [projectActiveDeviceIndex, setProjectActiveDeviceIndex] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchProject, setSearchProject] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({
    type: "",
    uuid: "",
    name: "",
  });
  const [deleting, setDeleting] = useState(false);

  const getToken = () => localStorage.getItem("token");

  const handleInvalidSession = () => {
    toast.error("Session expired or unauthorized. Please login again.");
    localStorage.clear();
    navigate("/urjabio-login");
  };

  const handleFetchError = (err) => {
    console.error("API error:", err);
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      handleInvalidSession();
    } else {
      toast.error("Failed to load data. Try again later.");
    }
  };

  const fetchLiveDeviceData = async (deviceId) => {
    const token = getToken();
    if (!deviceId || !token) return null;

    try {
      const res = await axios.get(`${DEVICE_DATA_URL}/${deviceId}/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const latestData = Array.isArray(res.data) ? res.data[0] : res.data;
      const sensors = latestData?.sensors || {};

      const dataObj = {};

      if (latestData?.timestamp) {
        dataObj.timestamp = latestData.timestamp;
      }

      if (
        sensors.Temperature !== undefined &&
        sensors.temperature === undefined
      ) {
        sensors.temperature = sensors.Temperature;
      }

      Object.entries(sensors).forEach(([key, value]) => {
        dataObj[key] = value ?? "-";
      });

      setDeviceDataCache((prev) => ({ ...prev, [deviceId]: dataObj }));
      return dataObj;
    } catch (err) {
      return null;
    }
  };

  const fetchProjects = async () => {
    const token = getToken();
    if (!token) return handleInvalidSession();
    try {
      setLoadingProjects(true);
      const res = await axios.get(PROJECTS_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const updatedProjects = data.map((p) => ({
        ...p,
        deviceNames: p.devices?.map((d) => d.name) || [],
      }));
      setProjects(updatedProjects);

      const indexMap = {};
      updatedProjects.forEach((p) => {
        if (p.projectUuid) indexMap[p.projectUuid] = 0;
      });
      setProjectActiveDeviceIndex(indexMap);

      const projectDataMap = {};
      await Promise.all(
        updatedProjects.map(async (p) => {
          if (p.devices?.length > 0) {
            const readings = await fetchLiveDeviceData(p.devices[0].deviceId);
            projectDataMap[p.projectUuid] = readings || {};
          }
        }),
      );
      setProjectDeviceData(projectDataMap);
      return updatedProjects;
    } catch (err) {
      handleFetchError(err);
      return [];
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchUsers = async (projectsList = []) => {
    const token = getToken();
    if (!token) return handleInvalidSession();
    try {
      setLoadingUsers(true);
      const res = await axios.get(USERS_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setUsers(data);

      // Build project names from already-loaded projects (avoids /projects/client 500s)
      const nameMap = {};
      data.forEach((u) => {
        const proj = projectsList.find(
          (pp) => pp.clientId === u.userUuid || pp.clientId === u.clientId,
        );
        nameMap[u.userUuid] = proj?.projectName || null;
      });
      setUserProjectMap(nameMap);

      const userDataMap = {};
      await Promise.all(
        data.map(async (u) => {
          const proj = projectsList.find(
            (pp) => pp.clientId === u.userUuid || pp.clientId === u.clientId,
          );
          if (proj?.devices?.length > 0) {
            const readings = await fetchLiveDeviceData(
              proj.devices[0].deviceId,
            );
            userDataMap[u.userUuid] = readings || {};
          }
        }),
      );
      setUserDeviceData(userDataMap);
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    (async () => {
      const loadedProjects = await fetchProjects();
      await fetchUsers(loadedProjects);
    })();
  }, []);

  const toggleMenu = (type, id) => {
    const key = `${type}-${id}`;
    setMenuOpen((prev) => (prev === key ? null : key));
  };

  const handleEditProject = (project) => {
    navigate("/urjabio/admin/add-project", { state: { editData: project } });
    setMenuOpen(null);
  };

  const handleEditUser = (user) => {
    navigate("/urjabio/admin/add-user", { state: { editData: user } });
    setMenuOpen(null);
  };

  // ✅ Updated delete + navigation behavior
  const handleDelete = (type, uuid, name = "") => {
    setDeleteTarget({ type, uuid, name });
    setShowDeleteModal(true);
    setMenuOpen(null);
  };

  const performDelete = async () => {
    if (!deleteTarget?.type || !deleteTarget?.uuid) return;
    setDeleting(true);
    const token = getToken();
    try {
      let url = "";
      if (deleteTarget.type === "User")
        url = `https://api.livestockify.com/api/urjabio/users/${deleteTarget.uuid}`;
      if (deleteTarget.type === "Project")
        url = `https://api.livestockify.com/api/urjabio/projects/${deleteTarget.uuid}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${deleteTarget.type} deleted successfully.`);
      if (deleteTarget.type === "User") {
        setUsers((prev) =>
          prev.filter((u) => u.userUuid !== deleteTarget.uuid),
        );
      } else {
        setProjects((prev) =>
          prev.filter((p) => p.projectUuid !== deleteTarget.uuid),
        );
      }
      setShowDeleteModal(false);
      setDeleteTarget({ type: "", uuid: "", name: "" });
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  const scrollLeft = (id) =>
    document.getElementById(id)?.scrollBy({ left: -300, behavior: "smooth" });
  const scrollRight = (id) =>
    document.getElementById(id)?.scrollBy({ left: 300, behavior: "smooth" });

  const filteredProjects = projects.filter(
    (p) =>
      p.projectName?.toLowerCase().includes(searchProject.toLowerCase()) ||
      p.projectLocation?.toLowerCase().includes(searchProject.toLowerCase()),
  );
  const isAdminUser = (u) =>
    String(u.roleType || u.role || "").toUpperCase() === "ADMIN";

  const filteredUsers = users.filter((u) =>
    `${u.userFirstname || ""} ${u.userLastname || ""} ${u.userEmail || ""}`
      .toLowerCase()
      .includes(searchUser.toLowerCase()),
  );
  const filteredAdmins = filteredUsers.filter(isAdminUser);
  const filteredClients = filteredUsers.filter((u) => !isAdminUser(u));

  if (loadingProjects || loadingUsers)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-11 w-11 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin"
            aria-hidden
          />
          <p className="text-sm font-medium text-slate-600">
            Loading dashboard…
          </p>
        </div>
      </div>
    );

  return (
    <div className="urjabio-dash relative w-full min-w-0 text-slate-900">
      {/* Full-viewport animated bg (covers shell padding gaps) */}
      <div className="ub-bg-layer" aria-hidden>
        <div className="ub-blob ub-blob-a" />
        <div className="ub-blob ub-blob-b" />
        <div className="ub-blob ub-blob-c" />
        <div className="ub-grid-fade" />
      </div>
      <style>{`
        .urjabio-dash .ub-bg-layer {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
          background:
            radial-gradient(ellipse 80% 60% at 10% 20%, rgba(52, 199, 89, 0.16), transparent 55%),
            radial-gradient(ellipse 70% 50% at 90% 10%, rgba(10, 92, 51, 0.1), transparent 50%),
            radial-gradient(ellipse 60% 45% at 70% 90%, rgba(52, 199, 89, 0.12), transparent 55%),
            linear-gradient(165deg, #f4fbf6 0%, #eef7f1 42%, #e7f3ec 100%);
        }
        .urjabio-dash .ub-blob {
          position: absolute;
          border-radius: 40% 60% 55% 45%;
          filter: blur(4px);
          opacity: 0.32;
          will-change: transform;
        }
        .urjabio-dash .ub-blob-a {
          width: 28rem;
          height: 28rem;
          top: -6rem;
          left: -5rem;
          background: radial-gradient(circle at 30% 30%, #7ee2a0, #34c759 55%, transparent 70%);
          animation: ub-dash-drift-a 14s ease-in-out infinite;
        }
        .urjabio-dash .ub-blob-b {
          width: 24rem;
          height: 24rem;
          right: -4rem;
          top: 22%;
          background: radial-gradient(circle at 60% 40%, #a8e6bf, #0a5c33 60%, transparent 72%);
          animation: ub-dash-drift-b 18s ease-in-out infinite;
        }
        .urjabio-dash .ub-blob-c {
          width: 20rem;
          height: 20rem;
          bottom: -4rem;
          left: 32%;
          background: radial-gradient(circle at 50% 50%, #c8f0d4, #2fa84f 50%, transparent 70%);
          animation: ub-dash-drift-c 16s ease-in-out infinite;
        }
        .urjabio-dash .ub-grid-fade {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(10, 92, 51, 0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(10, 92, 51, 0.035) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse 75% 65% at 50% 40%, black 25%, transparent 78%);
        }
        @keyframes ub-dash-drift-a {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(36px, 28px) rotate(8deg); }
        }
        @keyframes ub-dash-drift-b {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-30px, 40px) rotate(-6deg); }
        }
        @keyframes ub-dash-drift-c {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(24px, -20px) scale(1.08); }
        }
        @media (prefers-reduced-motion: reduce) {
          .urjabio-dash .ub-blob { animation: none !important; }
        }
        .urjabio-scroll ::-webkit-scrollbar { width: 4px; }
        .urjabio-scroll ::-webkit-scrollbar-track { background: transparent; border-radius: 4px; }
        .urjabio-scroll ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #22c55e, #0a5c33); border-radius: 4px; }
        .urjabio-scroll ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #16a34a, #064e2b); }
        .urjabio-scroll { scrollbar-width: thin; scrollbar-color: #22c55e transparent; }
      `}</style>

      <div className="relative z-10 flex w-full flex-col gap-4 sm:gap-5 lg:gap-6">
        {/* ---------------- PROJECT SECTION ---------------- */}
        <section className="rounded-3xl border border-white/70 bg-white/75 p-4 shadow-xl shadow-emerald-900/10 ring-1 ring-emerald-100/70 backdrop-blur-xl transition duration-300 hover:shadow-2xl hover:shadow-emerald-900/15 sm:p-6">
          <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-5 items-stretch">
            <div className="flex flex-row items-center justify-between gap-3 w-full min-w-0">
              <h2 className="text-base sm:text-xl font-bold uppercase tracking-[0.12em] text-slate-800 text-left flex-1 min-w-0 truncate pr-2">
                Projects
              </h2>
              <button
                type="button"
                aria-label="Add project"
                onClick={() => navigate("/urjabio/admin/add-project")}
                className="inline-flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#34C759] to-emerald-600 p-2.5 text-white shadow-md shadow-emerald-600/25 ring-1 ring-white/30 transition hover:brightness-110 hover:shadow-lg hover:shadow-emerald-600/30 active:scale-[0.98]"
              >
                <FolderPlus size={20} className="shrink-0" aria-hidden />
              </button>
            </div>
            <div className="relative w-full min-w-0 max-w-md sm:max-w-lg">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400"
                size={18}
                strokeWidth={2}
              />
              <input
                type="search"
                placeholder="Search projects"
                value={searchProject}
                onChange={(e) => setSearchProject(e.target.value)}
                className="w-full min-w-0 rounded-xl border-0 bg-slate-100/90 py-2.5 pl-10 pr-4 text-sm text-slate-800 shadow-inner ring-1 ring-slate-200/80 transition placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#34C759]/35 sm:py-3 sm:text-[15px]"
              />
            </div>
          </div>

          <div className="relative -mx-1 sm:mx-0">
            <button
              type="button"
              onClick={() => scrollLeft("projectScroll")}
              className="absolute left-0 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/75 text-slate-600 shadow-lg shadow-slate-300/40 backdrop-blur-xl transition hover:scale-105 hover:border-emerald-200 hover:text-emerald-700 sm:inline-flex"
              aria-label="Scroll projects left"
            >
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
            </button>

            <div
              id="projectScroll"
              className="flex gap-3 sm:gap-5 overflow-x-auto scrollbar-hide py-2 px-2 sm:px-12 snap-x snap-mandatory"
            >
              {filteredProjects.length > 0 ? (
                filteredProjects.map((p) => {
                  const activeIndex =
                    projectActiveDeviceIndex[p.projectUuid] ?? 0;
                  const currentDevice = p.devices?.[activeIndex];
                  const live =
                    (currentDevice &&
                      deviceDataCache[currentDevice.deviceId]) ||
                    projectDeviceData[p.projectUuid] ||
                    {};

                  const lastUpdated = formatTimestamp(live?.timestamp);

                  return (
                    <div
                      key={p.projectUuid}
                      className="group snap-start flex w-[300px] flex-none cursor-pointer flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-[#2ec155] via-[#34C759] to-emerald-700 shadow-xl shadow-emerald-900/25 ring-1 ring-white/30 transition duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-emerald-900/30"
                      onClick={() =>
                        navigate(`/urjabio/admin/metrics/${p.projectUuid}`)
                      }
                    >
                      <div className="flex items-start justify-between gap-2 px-4 py-3.5 text-white sm:px-5 sm:py-4">
                        <div className="min-w-0 flex flex-col items-start text-left">
                          <h3 className="text-base font-semibold leading-snug tracking-tight sm:text-lg">
                            {p.projectName || "Unnamed Project"}
                          </h3>
                          <p className="mt-0.5 text-sm text-white/85">
                            {p.projectLocation || "Unknown Location"}
                          </p>

                          {lastUpdated && (
                            <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-white/70">
                              Updated {lastUpdated}
                            </p>
                          )}
                        </div>

                        <div className="relative shrink-0">
                          {/* stop propagation so clicking menu doesn't navigate */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMenu("project", p.projectUuid);
                            }}
                            className="rounded-full p-1.5 text-white/90 transition hover:bg-white/15 hover:text-white"
                          >
                            <MoreVertical size={18} strokeWidth={2} />
                          </button>

                          {menuOpen === `project-${p.projectUuid}` && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-2xl border border-white/70 bg-white/85 py-1 text-slate-700 shadow-xl shadow-slate-900/15 backdrop-blur-xl"
                            >
                              <button
                                type="button"
                                onClick={() => handleEditProject(p)}
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition hover:bg-slate-50"
                              >
                                <Edit size={16} strokeWidth={2} /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDelete(
                                    "Project",
                                    p.projectUuid,
                                    p.projectName,
                                  )
                                }
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-red-600 transition hover:bg-red-50"
                              >
                                <Trash2 size={16} strokeWidth={2} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* project content */}

                      <div className="mt-auto px-3 pb-3 sm:px-4 sm:pb-4">
                        <div className="urjabio-scroll relative flex h-60 flex-col rounded-xl border border-white/40 bg-white/95 p-3 shadow-inner backdrop-blur-sm sm:h-[17rem] sm:p-4">
                          {currentDevice?.name && (
                            <p className="mb-2 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs sm:normal-case sm:tracking-normal">
                              <span className="text-slate-400">Device · </span>
                              {currentDevice.name}
                            </p>
                          )}

                          <div className="grid flex-1 grid-cols-2 gap-1.5 overflow-y-auto pr-1 content-start">
                            {live &&
                              Object.entries(live)
                                .filter(([key]) => {
                                  const k = key.toLowerCase();
                                  if (k === "timestamp") return false;
                                  if (
                                    k === "temperature" &&
                                    live.temperature !== undefined &&
                                    key !== "temperature"
                                  ) {
                                    return false;
                                  }
                                  return true;
                                })
                                .map(([key, value]) => (
                                  <div
                                    key={key}
                                    className="rounded-lg border border-slate-100/80 bg-white/80 px-2 py-1 text-xs font-medium text-slate-700 sm:px-2.5"
                                  >
                                    <p className="truncate capitalize text-slate-500">
                                      {key}
                                    </p>
                                    <p className="mt-0.5 truncate font-semibold tabular-nums text-emerald-700">
                                      {formatValue(value)}
                                    </p>
                                  </div>
                                ))}
                            {!currentDevice && (
                              <p className="col-span-2 px-2 text-sm text-slate-500 sm:px-3">
                                No devices added yet.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex min-h-[120px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
                  No projects found.
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => scrollRight("projectScroll")}
              className="absolute right-0 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/75 text-slate-600 shadow-lg shadow-slate-300/40 backdrop-blur-xl transition hover:scale-105 hover:border-emerald-200 hover:text-emerald-700 sm:inline-flex"
              aria-label="Scroll projects right"
            >
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
            </button>
          </div>

          {/* View All Projects Link */}
          <div className="mt-4 flex w-full sm:mt-5">
            <button
              type="button"
              onClick={() => navigate("/urjabio/admin/all-projects")}
              className="group ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 transition hover:text-emerald-800"
            >
              View all projects
              <ChevronRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.5}
              />
            </button>
          </div>
        </section>

        {/* ---------- USERS SECTION ---------- */}

        <section className="rounded-3xl border border-white/70 bg-white/75 p-4 shadow-xl shadow-emerald-900/10 ring-1 ring-emerald-100/70 backdrop-blur-xl transition duration-300 hover:shadow-2xl hover:shadow-emerald-900/15 sm:p-6">
          <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-5 items-stretch">
            <div className="flex flex-row items-center justify-between gap-3 w-full min-w-0">
              <h2 className="text-base sm:text-xl font-bold uppercase tracking-[0.12em] text-slate-800 text-left flex-1 min-w-0 truncate pr-2">
                Users
              </h2>
              <button
                type="button"
                aria-label="Add user"
                onClick={() => navigate("/urjabio/admin/add-user")}
                className="inline-flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#34C759] to-emerald-600 p-2.5 text-white shadow-md shadow-emerald-600/25 ring-1 ring-white/30 transition hover:brightness-110 hover:shadow-lg hover:shadow-emerald-600/30 active:scale-[0.98]"
              >
                <UserPlus size={20} className="shrink-0" aria-hidden />
              </button>
            </div>
            <div className="relative w-full min-w-0 max-w-md sm:max-w-lg">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400"
                size={18}
                strokeWidth={2}
              />
              <input
                type="search"
                placeholder="Search users"
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="w-full min-w-0 rounded-xl border-0 bg-slate-100/90 py-2.5 pl-10 pr-4 text-sm text-slate-800 shadow-inner ring-1 ring-slate-200/80 transition placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#34C759]/35 sm:py-3 sm:text-[15px]"
              />
            </div>
          </div>

          {/* ---------- CLIENT USERS (with assigned projects) ---------- */}
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
              Users
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              {filteredClients.length}
            </span>
          </div>

          <div className="relative -mx-1 sm:mx-0">
            <button
              type="button"
              onClick={() => scrollLeft("userScroll")}
              className="absolute left-0 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/75 text-slate-600 shadow-lg shadow-slate-300/40 backdrop-blur-xl transition hover:scale-105 hover:border-emerald-200 hover:text-emerald-700 sm:inline-flex"
              aria-label="Scroll users left"
            >
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
            </button>

            <div
              id="userScroll"
              className="flex gap-3 sm:gap-5 overflow-x-auto scrollbar-hide py-2 px-2 sm:px-12 snap-x snap-mandatory"
            >
              {filteredClients.length > 0 ? (
                filteredClients.map((u) => {
                  const userProject = projects.find(
                    (p) =>
                      p.clientId === u.userUuid || p.clientId === u.clientId,
                  );

                  const devices = userProject?.devices || [];
                  const activeIndex =
                    projectActiveDeviceIndex[userProject?.projectUuid] ?? 0;
                  const currentDevice = devices[activeIndex];
                  const live =
                    (currentDevice &&
                      deviceDataCache[currentDevice.deviceId]) ||
                    userDeviceData[u.userUuid] ||
                    {};

                  return (
                    <div
                      key={u.userUuid}
                      className="group snap-start flex w-[300px] flex-none flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-[#2ec155] via-[#34C759] to-emerald-700 shadow-xl shadow-emerald-900/25 ring-1 ring-white/30 transition duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-emerald-900/30"
                    >
                      {/* Green Header / background area */}
                      <div className="px-4 py-3.5 sm:px-5 sm:py-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex flex-col items-start text-left">
                            <h3 className="text-base font-semibold leading-snug tracking-tight text-white sm:text-lg">
                              {`${u.userFirstname || ""} ${
                                u.userLastname || ""
                              }`.trim() || "User Name"}
                            </h3>
                            <p className="mt-0.5 text-sm text-white/85">
                              {u.address || "Unknown Location"}
                            </p>
                          </div>

                          <div className="relative shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu("user", u.userUuid);
                              }}
                              aria-label="User menu"
                              className="rounded-full p-1.5 text-white/90 transition hover:bg-white/15 hover:text-white"
                            >
                              <MoreVertical size={18} strokeWidth={2} />
                            </button>

                            {menuOpen === `user-${u.userUuid}` && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-2xl border border-white/70 bg-white/85 py-1 text-slate-700 shadow-xl shadow-slate-900/15 backdrop-blur-xl"
                              >
                                <button
                                  type="button"
                                  onClick={() => handleEditUser(u)}
                                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition hover:bg-slate-50"
                                >
                                  <Edit size={16} strokeWidth={2} /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDelete(
                                      "User",
                                      u.userUuid,
                                      `${u.userFirstname || ""} ${
                                        u.userLastname || ""
                                      }`,
                                    )
                                  }
                                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-red-600 transition hover:bg-red-50"
                                >
                                  <Trash2 size={16} strokeWidth={2} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Centered white panel */}
                      <div className="mt-auto px-3 pb-3 sm:px-4 sm:pb-4">
                        <div className="urjabio-scroll relative flex h-60 flex-col rounded-xl border border-white/40 bg-white/95 p-3 shadow-inner backdrop-blur-sm sm:h-[17rem] sm:p-4">
                          <p className="mb-2 text-center text-sm font-semibold tracking-tight text-slate-800 sm:mb-3 sm:text-base">
                            {userProjectMap[u.userUuid] ?? "Project Name"}
                          </p>

                          {currentDevice?.name && (
                            <p className="mb-2 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs sm:normal-case sm:tracking-normal">
                              <span className="text-slate-400">Device · </span>
                              {currentDevice.name}
                            </p>
                          )}

                          <div className="grid flex-1 grid-cols-2 gap-1.5 overflow-y-auto pr-1 content-start">
                            {Object.entries(live || {})
                              .filter(
                                ([key]) => key.toLowerCase() !== "timestamp",
                              )
                              .map(([key, value]) => {
                                if (key.toLowerCase() === "temperature") {
                                  if (
                                    live.temperature !== undefined &&
                                    key !== "temperature"
                                  )
                                    return null;
                                }

                                return (
                                  <div
                                    key={key}
                                    className="rounded-lg border border-slate-100/80 bg-white/80 px-2 py-1 text-xs font-medium text-slate-700 sm:px-2.5"
                                  >
                                    <p className="truncate capitalize text-slate-500">
                                      {key}
                                    </p>
                                    <p className="mt-0.5 truncate font-semibold tabular-nums text-emerald-700">
                                      {formatValue(value)}
                                    </p>
                                  </div>
                                );
                              })}
                            {!currentDevice && (
                              <p className="col-span-2 px-2 text-sm text-slate-500 sm:px-3">
                                No devices linked to this project.
                              </p>
                            )}
                          </div>

                          {/* Arrows + dots centered at bottom */}
                          {devices.length > 1 && (
                            <div className="relative bottom-0 left-0 right-0 flex items-center justify-center gap-4 pt-1">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const total = devices.length;
                                  const current =
                                    projectActiveDeviceIndex[
                                      userProject?.projectUuid
                                    ] ?? 0;
                                  const prevIndex =
                                    (current - 1 + total) % total;

                                  setProjectActiveDeviceIndex((prev) => ({
                                    ...prev,
                                    [userProject?.projectUuid]: prevIndex,
                                  }));

                                  const prevDevice = devices[prevIndex];
                                  if (prevDevice?.deviceId)
                                    fetchLiveDeviceData(prevDevice.deviceId);
                                }}
                                className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-emerald-600"
                              >
                                <ChevronLeft size={22} strokeWidth={2} />
                              </button>

                              <div className="flex items-center gap-1.5">
                                {devices.map((_, i) => (
                                  <span
                                    key={i}
                                    className={`h-2 w-2 rounded-full transition ${
                                      i ===
                                      (projectActiveDeviceIndex[
                                        userProject?.projectUuid
                                      ] ?? 0)
                                        ? "scale-110 bg-emerald-500 shadow-sm shadow-emerald-500/50"
                                        : "bg-slate-300"
                                    }`}
                                  />
                                ))}
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const total = devices.length;
                                  const current =
                                    projectActiveDeviceIndex[
                                      userProject?.projectUuid
                                    ] ?? 0;
                                  const nextIndex = (current + 1) % total;

                                  setProjectActiveDeviceIndex((prev) => ({
                                    ...prev,
                                    [userProject?.projectUuid]: nextIndex,
                                  }));

                                  const nextDevice = devices[nextIndex];
                                  if (nextDevice?.deviceId)
                                    fetchLiveDeviceData(nextDevice.deviceId);
                                }}
                                className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-emerald-600"
                              >
                                <ChevronRight size={22} strokeWidth={2} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex min-h-[120px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
                  No client users found.
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => scrollRight("userScroll")}
              className="absolute right-0 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/75 text-slate-600 shadow-lg shadow-slate-300/40 backdrop-blur-xl transition hover:scale-105 hover:border-emerald-200 hover:text-emerald-700 sm:inline-flex"
              aria-label="Scroll users right"
            >
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
            </button>
          </div>

          {/* ---------- ADMINS (below clients, compact cards) ---------- */}
          {filteredAdmins.length > 0 && (
            <div className="mt-5 sm:mt-6">
              <div className="mb-2.5 flex items-center gap-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                  Admins
                </h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                  {filteredAdmins.length}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {filteredAdmins.map((u) => (
                  <div
                    key={u.userUuid}
                    className="relative rounded-xl border border-slate-200/80 bg-white/95 px-4 py-3 shadow-sm ring-1 ring-slate-100"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate text-sm font-semibold text-slate-800">
                          {`${u.userFirstname || ""} ${
                            u.userLastname || ""
                          }`.trim() || "Admin"}
                        </h4>
                        <p className="mt-1 truncate text-xs text-slate-500">
                          {u.userEmail || "-"}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-slate-400">
                          {u.userPhoneno || "-"}
                        </p>
                        {u.address && (
                          <p className="mt-0.5 truncate text-[11px] text-slate-400">
                            {u.address}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-100">
                          Admin
                        </span>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMenu("user", u.userUuid);
                            }}
                            aria-label="Admin menu"
                            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          >
                            <MoreVertical size={16} strokeWidth={2} />
                          </button>
                          {menuOpen === `user-${u.userUuid}` && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="absolute right-0 z-50 mt-1 w-28 overflow-hidden rounded-lg border border-slate-200 bg-white py-0.5 text-slate-700 shadow-lg"
                            >
                              <button
                                type="button"
                                onClick={() => handleEditUser(u)}
                                className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-xs hover:bg-slate-50"
                              >
                                <Edit size={13} /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDelete(
                                    "User",
                                    u.userUuid,
                                    `${u.userFirstname || ""} ${
                                      u.userLastname || ""
                                    }`,
                                  )
                                }
                                className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={13} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex w-full sm:mt-5">
            <button
              type="button"
              onClick={() => navigate("/urjabio/admin/all-users")}
              className="group ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 transition hover:text-emerald-800"
            >
              View all users
              <ChevronRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.5}
              />
            </button>
          </div>
        </section>
      </div>

      {/* ---------- Delete Confirmation Modal ---------- */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
            onClick={() => !deleting && setShowDeleteModal(false)}
            aria-hidden
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            className="relative z-[70] w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xl shadow-slate-900/15 sm:p-6"
          >
            <h3
              id="delete-modal-title"
              className="text-lg font-bold tracking-tight text-slate-900"
            >
              Delete {deleteTarget.type?.toLowerCase() || "item"}?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              This cannot be undone. All related data may be removed.
            </p>

            {deleteTarget.name && (
              <p className="mt-4 rounded-xl bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-800 ring-1 ring-slate-100">
                <span className="font-normal text-slate-500">Name · </span>
                {deleteTarget.name}
              </p>
            )}

            <div className="mt-6 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={performDelete}
                disabled={deleting}
                className={`w-full rounded-xl py-3 text-sm font-semibold text-white shadow-md transition ${
                  deleting
                    ? "cursor-not-allowed bg-amber-400/80"
                    : "bg-gradient-to-r from-amber-500 to-orange-500 hover:brightness-105 active:scale-[0.99]"
                }`}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>

              <button
                type="button"
                onClick={() => !deleting && setShowDeleteModal(false)}
                className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
